import React, { useState, useEffect, useCallback } from 'react';
import {
  Cpu, Shield, Activity, AlertTriangle, CheckCircle, XCircle,
  RefreshCw, Plus, Wifi, Radio, Server, Eye, Lock, Unlock,
  ChevronDown, ChevronUp, Search, Filter, Zap, BarChart3,
  Monitor, Smartphone, Home, Factory, Heart, Car, Building
} from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { IoTVulnerabilitiesTab, IoTAccessControlTab } from './iot/IoTSecurityTabs';
import IoTOverviewTab from './iotsec/IoTOverviewTab';
import IoTEndpointsTab from './iotsec/IoTEndpointsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IoTSecurity = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [endpoints, setEndpoints] = useState([]);
  const [categories, setCategories] = useState({});
  const [overview, setOverview] = useState(null);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showTrafficModal, setShowTrafficModal] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [trafficResult, setTrafficResult] = useState(null);
  const [anomalyResult, setAnomalyResult] = useState(null);

  const [registerForm, setRegisterForm] = useState({
    device_name: '',
    device_type: 'sensor',
    category: 'smart_home',
    manufacturer: '',
    model: '',
    firmware_version: '1.0.0',
    ip_address: '',
    mac_address: '',
    location: '',
    owner_id: 'demo-user-001',
    protocols: ['mqtt', 'https'],
    capabilities: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [overviewRes, endpointsRes, categoriesRes, vulnRes] = await Promise.all([
        authFetch(`${API_URL}/api/securesphere/iot-security/dashboard/overview`),
        authFetch(`${API_URL}/api/securesphere/iot-security/endpoints?owner_id=demo-user-001`),
        authFetch(`${API_URL}/api/securesphere/iot-security/categories`),
        authFetch(`${API_URL}/api/securesphere/iot-security/firmware/vulnerabilities`)
      ]);
      
      const overviewData = await overviewRes.json();
      const endpointsData = await endpointsRes.json();
      const categoriesData = await categoriesRes.json();
      const vulnData = await vulnRes.json();
      
      setOverview(overviewData);
      setEndpoints(endpointsData.endpoints || []);
      setCategories(categoriesData.categories || {});
      setVulnerabilities(vulnData.vulnerabilities || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const registerEndpoint = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${API_URL}/api/securesphere/iot-security/endpoints/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerForm)
      });
      if (response.ok) {
        setShowRegisterModal(false);
        fetchData();
        setRegisterForm({
          device_name: '',
          device_type: 'sensor',
          category: 'smart_home',
          manufacturer: '',
          model: '',
          firmware_version: '1.0.0',
          ip_address: '',
          mac_address: '',
          location: '',
          owner_id: 'demo-user-001',
          protocols: ['mqtt', 'https'],
          capabilities: []
        });
      }
    } catch (error) {
    }
  };

  const scanFirmware = async (endpoint) => {
    try {
      const response = await authFetch(`${API_URL}/api/securesphere/iot-security/firmware/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: endpoint.id,
          firmware_version: endpoint.firmware_version,
          manufacturer: endpoint.manufacturer
        })
      });
      const data = await response.json();
      setScanResult(data);
      setShowScanModal(true);
      fetchData();
    } catch (error) {
    }
  };

  const analyzeTraffic = async (endpoint) => {
    try {
      // Generate sample traffic data
      const sampleTraffic = Array.from({ length: 10 }, (_, i) => ({
        timestamp: new Date(Date.now() - i * 60000).toISOString(),
        bytes_in: Math.floor(Math.random() * 10000),
        bytes_out: Math.floor(Math.random() * 5000),
        protocol: ['mqtt', 'https', 'http'][Math.floor(Math.random() * 3)],
        destination: ['cloud.iot.aws', 'api.device.local', 'telemetry.service'][Math.floor(Math.random() * 3)],
        port: [443, 8883, 80][Math.floor(Math.random() * 3)]
      }));

      const response = await authFetch(`${API_URL}/api/securesphere/iot-security/traffic/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: endpoint.id,
          traffic_samples: sampleTraffic,
          duration_minutes: 60
        })
      });
      const data = await response.json();
      setTrafficResult(data);
      setShowTrafficModal(true);
    } catch (error) {
    }
  };

  const detectAnomalies = async (endpoint) => {
    try {
      // Generate sample data points
      const dataPoints = Array.from({ length: 5 }, (_, i) => ({
        type: ['cpu', 'memory', 'network', 'temperature'][Math.floor(Math.random() * 4)],
        value: Math.floor(Math.random() * 150),
        threshold: 100
      }));

      const response = await fetch(
        `${API_URL}/api/securesphere/iot-security/anomalies/detect?device_id=${endpoint.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataPoints)
        }
      );
      const data = await response.json();
      setAnomalyResult(data);
      fetchData();
    } catch (error) {
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      smart_home: Home,
      industrial: Factory,
      healthcare: Heart,
      automotive: Car,
      infrastructure: Building
    };
    return icons[category] || Cpu;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      medium: 'bg-yellow-100 text-yellow-700',
      low: 'bg-green-100 text-green-700'
    };
    return colors[severity] || colors.medium;
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'endpoints', name: 'Endpoints', icon: Cpu },
    { id: 'monitoring', name: 'Monitoring', icon: Activity },
    { id: 'vulnerabilities', name: 'Vulnerabilities', icon: AlertTriangle },
    { id: 'access-control', name: 'Access Control', icon: Lock }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold text-gray-900">IoT Security</h2>
            <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs font-semibold rounded-full">
              ADVANCED
            </span>
          </div>
          <p className="text-gray-600">Comprehensive IoT endpoint monitoring and protection</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => setShowRegisterModal(true)} className="bg-cyan-600 hover:bg-cyan-700">
            <Plus className="h-4 w-4 mr-2" />
            Register Endpoint
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-cyan-100 text-cyan-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.name}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && overview && (
        <IoTOverviewTab overview={overview} />
      )}

      {activeTab === 'endpoints' && (
        <IoTEndpointsTab endpoints={endpoints} categories={categories} fetchEndpoints={fetchEndpoints} registerEndpoint={registerEndpoint} />
      )}

      {activeTab === 'monitoring' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Real-Time Monitoring</h3>
            <p className="text-gray-600 mb-4">Select an endpoint to view monitoring data</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Traffic Analysis Results */}
              {trafficResult && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Latest Traffic Analysis</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Samples Analyzed</span>
                      <span className="font-medium">{trafficResult.samples_analyzed}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total Bytes In</span>
                      <span className="font-medium">{(trafficResult.summary?.total_bytes_in / 1024).toFixed(2)} KB</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total Bytes Out</span>
                      <span className="font-medium">{(trafficResult.summary?.total_bytes_out / 1024).toFixed(2)} KB</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Threat Level</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(trafficResult.threat_level)}`}>
                        {trafficResult.threat_level}
                      </span>
                    </div>
                    {trafficResult.suspicious_patterns?.length > 0 && (
                      <div className="mt-2 p-2 bg-yellow-50 rounded">
                        <p className="text-xs font-medium text-yellow-700">Suspicious Patterns:</p>
                        {trafficResult.suspicious_patterns.map((p, i) => (
                          <p key={p} className="text-xs text-yellow-600">• {p.description}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Anomaly Detection Results */}
              {anomalyResult && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Latest Anomaly Detection</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Data Points Analyzed</span>
                      <span className="font-medium">{anomalyResult.data_points_analyzed}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Anomalies Found</span>
                      <span className="font-medium">{anomalyResult.anomalies_found}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Risk Level</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(anomalyResult.risk_level)}`}>
                        {anomalyResult.risk_level}
                      </span>
                    </div>
                    {anomalyResult.anomalies?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {anomalyResult.anomalies.slice(0, 3).map((a, i) => (
                          <div key={a} className="p-2 bg-red-50 rounded text-xs">
                            <span className="font-medium text-red-700">{a.type}:</span>
                            <span className="text-red-600 ml-1">Value {a.value} exceeded threshold {a.threshold}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {!trafficResult && !anomalyResult && (
              <div className="text-center py-8 text-gray-500">
                <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Run traffic analysis or anomaly detection on an endpoint to see results</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vulnerabilities Tab */}
      {activeTab === 'vulnerabilities' && (
        <IoTVulnerabilitiesTab vulnerabilities={vulnerabilities} scanResult={scanResult} />
      )}

      {/* Access Control Tab */}
      {activeTab === 'access-control' && (
        <IoTAccessControlTab />
      )}

      {/* Register Endpoint Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">Register IoT Endpoint</h3>
            </div>
            <form onSubmit={registerEndpoint} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Device Name</label>
                  <input
                    type="text"
                    required
                    value={registerForm.device_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, device_name: e.target.value })}
                    placeholder="e.g., Living Room Thermostat"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={registerForm.category}
                    onChange={(e) => setRegisterForm({ ...registerForm, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                  >
                    {Object.keys(categories).map((cat) => (
                      <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                  <input
                    type="text"
                    required
                    value={registerForm.manufacturer}
                    onChange={(e) => setRegisterForm({ ...registerForm, manufacturer: e.target.value })}
                    placeholder="e.g., Nest"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                  <input
                    type="text"
                    required
                    value={registerForm.model}
                    onChange={(e) => setRegisterForm({ ...registerForm, model: e.target.value })}
                    placeholder="e.g., Learning Thermostat"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Firmware Version</label>
                  <input
                    type="text"
                    required
                    value={registerForm.firmware_version}
                    onChange={(e) => setRegisterForm({ ...registerForm, firmware_version: e.target.value })}
                    placeholder="e.g., 1.2.3"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                  <input
                    type="text"
                    value={registerForm.ip_address}
                    onChange={(e) => setRegisterForm({ ...registerForm, ip_address: e.target.value })}
                    placeholder="e.g., 192.168.1.100"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={registerForm.location}
                  onChange={(e) => setRegisterForm({ ...registerForm, location: e.target.value })}
                  placeholder="e.g., Building A, Floor 2"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowRegisterModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                  Register Endpoint
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Traffic Analysis Modal */}
      {showTrafficModal && trafficResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Traffic Analysis Results</h3>
              <button onClick={() => setShowTrafficModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Duration</p>
                  <p className="text-lg font-bold">{trafficResult.duration_minutes} min</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Samples</p>
                  <p className="text-lg font-bold">{trafficResult.samples_analyzed}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Bandwidth</p>
                  <p className="text-lg font-bold">{trafficResult.summary?.bandwidth_utilization} KB/s</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Destinations</p>
                  <p className="text-lg font-bold">{trafficResult.summary?.unique_destinations}</p>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg ${
                trafficResult.threat_level === 'low' ? 'bg-green-50' :
                trafficResult.threat_level === 'medium' ? 'bg-yellow-50' : 'bg-red-50'
              }`}>
                <div className="flex items-center gap-2">
                  {trafficResult.threat_level === 'low' ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  )}
                  <span className="font-medium capitalize">{trafficResult.threat_level} Threat Level</span>
                </div>
                {trafficResult.suspicious_patterns?.length > 0 && (
                  <div className="mt-2">
                    {trafficResult.suspicious_patterns.map((p, i) => (
                      <p key={p} className="text-sm">⚠️ {p.description}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Firmware Scan Modal */}
      {showScanModal && scanResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Firmware Scan Results</h3>
              <button onClick={() => setShowScanModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Firmware Version</p>
                  <p className="font-mono font-bold">{scanResult.firmware_version}</p>
                </div>
                <span className={`px-3 py-1 rounded-full font-medium ${getSeverityColor(scanResult.overall_risk)}`}>
                  {scanResult.overall_risk.toUpperCase()} RISK
                </span>
              </div>
              
              <div className="text-center p-4">
                <p className="text-4xl font-bold text-red-600">{scanResult.vulnerabilities_found}</p>
                <p className="text-gray-500">Vulnerabilities Found</p>
              </div>
              
              {scanResult.vulnerabilities?.length > 0 && (
                <div className="space-y-2">
                  {scanResult.vulnerabilities.map((v, i) => (
                    <div key={v.id || v.name || i} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm">{v.cve_id}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(v.severity)}`}>
                          {v.severity}
                        </span>
                      </div>
                      <p className="text-sm mt-1">{v.name}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {scanResult.recommendations && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Recommendations</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {scanResult.recommendations.map((rec, i) => (
                      <li key={rec}>• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IoTSecurity;
